import { Application } from "../../model/application.model";
import { User } from "../../../core/user/user.types";
import { CommonBaseModel, DateTimeRange, Pagination } from "../../model/common.model";
import { DateTime } from "luxon";
import { WorkflowHistoryItem } from "../../model/workflow.model";
import { hasValue } from "app/shared/global.functions";
import { ControlErrors } from "app/modules/forms/application-editor/validation";
import { GuestUser } from "app/shared/model";

export interface ISaveApplicationAuthenticationInput {
    applicationId: string,
    formId: string
}

export interface ISurveyResultInput {
    formId: string;
    formVersion: number | null;
    applicationId: string;
    applicationData: string;
}

export interface ICreateApplicationInput {
    formId: string;
    saveApplication: boolean;
    formVersion: number | null;
}

export interface IFindApplicationsInput {
    formId: string;
    filter: ApplicationSearchFilter;
    pagination: Pagination;
}

export interface IValidateApplicationInput {
    formId: string,
    formVersion: number | undefined,
    applicationId: string,
    data: any,
    pageId: number,
    subpageControlId: number | null
}


export interface ISaveSubmitApplicationInput {
    formId: string,
    formVersion: number,
    applicationId: string,
    applicationIdDisplay: string,
    offlineApplicationId: string,
    isDirty?: boolean,
    data: any,
    created: DateTime,
    modified?: DateTime,
    eventType?: 'save' | 'submit',
    user?: User,
    userId: string,
    applicationState: string,
}

export interface ISaveApplicationInput extends ISaveSubmitApplicationInput {
    publicDidAuthenticate: boolean,
    publicDidCreateAccount: boolean,
    isAutoSave: boolean,
    pageId: number | null,
    subpageId: number | null,
}

export interface ISubmitApplicationInput extends ISaveSubmitApplicationInput {
    metadata: any,
    submitPageId: number | null,
}

export interface ISubmitApplicationMetadataInput {
    requestedState: string,
    assignee: string[],
    assigneeType: string,
}

// export class SubmitSaveDto implements ISubmitApplicationInput, ISaveApplicationInput {
//     formId: string;
//     formVersion: number;
//     applicationId: string;
//     applicationIdDisplay: string;
//     offlineApplicationId: string;
//     isDirty?: boolean;
//     data: any;
//     created: DateTime;
//     modified?: DateTime;
//     eventType: 'save' | 'submit';
//     user?: User;
//     userId: string;
//     applicationState: string;
//     // Save
//     publicDidAuthenticate: boolean;
//     publicDidCreateAccount: boolean;
//     isAutoSave: boolean;
//     pageId: number;
//     subpageId: number;
//     // Submit
//     metadata: any;
//     submitPageId: number;
// }

export interface IGetApplicationSummaryInput {
    formId: string,
    filter: ApplicationSearchFilter
}

export interface ISaveApplicationBySearchControlInput {
    formId: string,
    formVersion: number | null,
    applicationId: string,
    controlName: string,
    searchTerm: string,
    selectedItemIndex: number,
}

export interface ISaveApplicationResponse {
    success: boolean,
    user: User,
    application: Application
}

export class SaveApplicationResponse implements ISaveApplicationResponse {
    success: boolean = false;
    user: User = null;
    application: Application = null;

    constructor(data?: ISaveApplicationResponse) {
        if (data) {
            for (let property in data) {
                if (data.hasOwnProperty(property))
                    (<any>this)[property] = (<any>data)[property];
            }
        }
    }

    init(_data?: any) {
        if (_data) {
            this.application = _data["application"] ? Application.fromJS(_data["application"]) : <any>undefined;
            this.success = _data["Success"];
            this.user = _data["User"] ? User.fromJS(_data["User"]) : <any>undefined;
        }
    }

    static fromJS(data: any): SaveApplicationResponse {
        data = typeof data === 'object' ? data : {};
        let result = new SaveApplicationResponse();
        result.init(data);
        return result;
    }

    toJSON(data?: any) {
        data = typeof data === 'object' ? data : {};
        data["application"] = this.application ? this.application.toJSON() : <any>undefined;
        data["Success"] = this.success;
        data["User"] = this.user ? JSON.stringify(this.user) : <any>undefined;
        return data;
    }
}

export interface ICreateApplicationResponse {
    success: boolean;
    application: Application;
}

export class CreateApplicationResponse implements ICreateApplicationResponse {
    success: boolean;
    application: Application;

    constructor(data?: ICreateApplicationResponse) {
        if (data) {
            for (let property in data) {
                if (data.hasOwnProperty(property))
                    (<any>this)[property] = (<any>data)[property];
            }
        }
    }

    init(_data?: any) {
        if (_data) {
            this.application = _data["application"] ? Application.fromJS(_data["application"]) : <any>undefined;
            this.success = _data["Success"];
        }
    }

    static fromJS(data: any): CreateApplicationResponse {
        data = typeof data === 'object' ? data : {};
        let result = new CreateApplicationResponse();
        result.init(data);
        return result;
    }

    toJSON(data?: any) {
        data = typeof data === 'object' ? data : {};
        data["application"] = this.application ? this.application.toJSON() : <any>undefined;
        data["Success"] = this.success;
        return data;
    }
}

export type AssignedApplicationList = {
    [key: string]: Application[]
}

export type ProductApplicationList = {
    id: string;
    name: string;
    offline: boolean;
    applications: Application[];
}

export interface IGetAssignedApplicationsResponse {
    success: boolean,
    userApplications: { [key: string]: Application[]; },
    organisationApplications: { [key: string]: Application[]; },
    count: number,
    organisationsRelativeUrl: { [key: string]: string; }
}

export class GetApplicationCountResponse extends CommonBaseModel
{
    success: boolean = false;
    count: ApplicationCount = new ApplicationCount();

    init(_data?: any): this {
        super.init(_data);
        if (_data['Count']) {
            this.count = (new ApplicationCount()).init(_data['Count']);
        }
        return this;
    }
}

export class ApplicationCount extends CommonBaseModel
{
    assigned: number = 0;
    submitted: number = 0;
}

export class GetAssignedApplicationsResponse extends CommonBaseModel implements IGetAssignedApplicationsResponse {
    success: boolean = false;
    userApplications: AssignedApplicationList = {};
    organisationApplications: AssignedApplicationList = {};
    count: number = 0;
    organisationsRelativeUrl: { [key: string]: string; } = {};

    init(_data?: any): this {
        super.init(_data);

        this.userApplications = {};
        this.organisationApplications = {};
        this.tryInitApplicationList(this.userApplications, _data, 'userApplications');
        this.tryInitApplicationList(this.organisationApplications, _data, 'organisationApplications');

        return this;
    }

    private tryInitApplicationList(internalProperty: AssignedApplicationList, _data: any, propertyName: string) {
        if (_data && _data[propertyName]) {
            const source = _data[propertyName];
            for (let productName in source) {
                if (source.hasOwnProperty(productName)) {
                    internalProperty[productName] = [];
                    for (let application of source[productName]) {
                        internalProperty[productName].push(Application.fromJS(application));
                    }
                }
            }
        }
    }
}

export class GetApplicationResponse implements IGetApplicationResponse {
    success: boolean = false;
    application: Application = null;

    constructor(data?: IGetApplicationResponse) {
        if (data) {
            for (let property in data) {
                if (data.hasOwnProperty(property))
                    (<any>this)[property] = (<any>data)[property];
            }
        }
    }

    init(_data?: any) {
        if (_data) {
            this.application = _data["application"] ? Application.fromJS(_data["application"]) : <any>undefined;
            this.success = _data["Success"];
        }
    }

    static fromJS(data: any): GetApplicationResponse {
        data = typeof data === 'object' ? data : {};
        let result = new GetApplicationResponse();
        result.init(data);
        return result;
    }

    toJSON(data?: any) {
        data = typeof data === 'object' ? data : {};
        data["application"] = this.application ? this.application.toJSON() : <any>undefined;
        data["Success"] = this.success;
        return data;
    }
}

export interface IGetApplicationResponse {
    success: boolean | undefined;
    application: Application | undefined;
}

export interface ISubmitApplicationResponse {
    success: boolean,
    valid: boolean,
    application: Application
    redirectionUrl: string
    firstPage: FirstValidationPage | null
    errors: ControlErrors;
}

export type FirstValidationPage = {
    pageId: number | null;
    subpage?: number | null;
    subpageId?: number | null;
}

export class SubmitApplicationResponse implements ISubmitApplicationResponse {
    success: boolean = false;
    valid: boolean = false;
    application: Application = null;
    redirectionUrl: string = null;
    firstPage: FirstValidationPage = { pageId: null, subpage: null, subpageId: null };
    errors: ControlErrors = null;

    constructor(data?: ISubmitApplicationResponse) {
        if (data) {
            for (let property in data) {
                if (data.hasOwnProperty(property))
                    (<any>this)[property] = (<any>data)[property];
            }
        }
    }

    init(_data?: any) {
        if (_data) {
            this.application = _data["application"] ? Application.fromJS(_data["application"]) : <any>undefined;
            this.success = _data["Success"];
            this.valid = _data["valid"];
            this.errors = _data["errors"];
            this.firstPage = _data["firstPage"];
            this.redirectionUrl = _data["redirectionUrl"];
        }
    }

    static fromJS(data: any): SubmitApplicationResponse {
        data = typeof data === 'object' ? data : {};
        let result = new SubmitApplicationResponse();
        result.init(data);
        return result;
    }

    toJSON(data?: any) {
        data = typeof data === 'object' ? data : {};
        data["application"] = this.application ? this.application.toJSON() : <any>undefined;
        data["Success"] = this.success;
        data["valid"] = this.valid;
        data["errors"] = this.errors;
        data["firstPage"] = this.firstPage;
        data["redirectionUrl"] = this.redirectionUrl;
        return data;
    }
}

export interface IValidateApplicationResponse {
    success: boolean;
    valid: boolean;
    redirectionRequired: boolean;
    firstPage: FirstValidationPage;
    errors: ControlErrors;
}

export class ValidateApplicationResponse implements IValidateApplicationResponse {
    success: boolean;
    valid: boolean;
    redirectionRequired: boolean;
    firstPage: FirstValidationPage;
    errors: ControlErrors;

    constructor(data?: IValidateApplicationResponse) {
        if (data) {
            for (let property in data) {
                if (data.hasOwnProperty(property))
                    (<any>this)[property] = (<any>data)[property];
            }
        }
    }

    init(_data?: any) {
        if (_data) {
            this.success = _data["Success"];
            this.valid = _data["valid"];
            this.errors = _data["errors"];
            this.firstPage = _data["firstPage"];
            this.redirectionRequired = _data["redirectionRequired"];
        }
    }

    static fromJS(data: any): ValidateApplicationResponse {
        data = typeof data === 'object' ? data : {};
        let result = new ValidateApplicationResponse();
        result.init(data);
        return result;
    }

    toJSON(data?: any) {
        data = typeof data === 'object' ? data : {};
        data["Success"] = this.success;
        data["valid"] = this.valid;
        data["errors"] = this.errors;
        data["firstPage"] = this.firstPage;
        data["redirectionRequired"] = this.redirectionRequired;
        return data;
    }
}

export interface IApplicationSearchFilter {
    applicationId: string,
    applicationIds: string[],
    applicationStates: string[],
    applicationCreated: DateTimeRange,
    applicationModified: DateTimeRange,
    applicationData?: any,
    applicationDataDates?: { [key: string]: DateTimeRange; },
    originator?: string,
    // stateTransitionDate?: DateTimeRange,
    // createdByWorkflowAction?: WorkflowActionLink
}

export class ApplicationSearchFilter implements IApplicationSearchFilter {
    applicationId: string = '';
    applicationIds: string[] = [];
    applicationStates: string[] = [];
    applicationCreated: DateTimeRange = null;
    applicationModified: DateTimeRange = null;
    applicationData?: any = null;
    applicationDataDates?: { [key: string]: DateTimeRange; };
    // stateTransitionDate?: DateTimeRange;
    originator?: string;
    // createdByWorkflowAction?: WorkflowActionLink;

    constructor(data?: IApplicationSearchFilter) {
        if (data) {
            for (let property in data) {
                if (data.hasOwnProperty(property))
                    (<any>this)[property] = (<any>data)[property];
            }
        }
    }

    init(_data?: any): this {
        if (_data) {
            this.applicationId = _data["applicationId"];
            this.applicationIds = _data["applicationIds"];
            this.applicationStates = _data["applicationStates"];
            this.applicationCreated = new DateTimeRange(_data["applicationCreated"]);
            this.applicationModified = new DateTimeRange(_data["applicationModified"]);
            this.applicationData = _data["applicationData"];
            this.applicationDataDates = _data["applicationDataDates"];
            // this.stateTransitionDate = _data["stateTransitionDate"];
            this.originator = _data["originator"];
            // this.createdByWorkflowAction = _data["createdByWorkflowAction"];
        }

        return this;
    }

    static fromJS(data: any): ApplicationSearchFilter {
        data = typeof data === 'object' ? data : {};
        let result = new ApplicationSearchFilter();
        result.init(data);
        return result;
    }

    toJSON(data?: any) {
        data = typeof data === 'object' ? data : {};
        data["applicationStates"] = this.applicationStates;
        data["applicationCreated"] = this.applicationCreated;
        data["applicationModified"] = this.applicationModified;
        data["applicationIds"] = this.applicationIds;
        data["applicationData"] = this.applicationData;
        data["applicationDataDates"] = this.applicationDataDates;
        // data["stateTransitionDate"] = this.stateTransitionDate;
        data["originator"] = this.originator;
        // data["createdByWorkflowAction"] = this.createdByWorkflowAction;
        return data;
    }

    hasValue(): boolean {
        return hasValue(this.applicationId)
            || hasValue(this.applicationIds)
            || hasValue(this.applicationStates)
            || hasValue(this.originator)
            || hasValue(this.applicationCreated?.from)
            || hasValue(this.applicationCreated?.to)
            || hasValue(this.applicationModified?.from)
            || hasValue(this.applicationModified?.to);
    }
}

export interface IFindApplicationsResponse {
    success: boolean,
    applications: Application[],
    pagination: Pagination,
    relativeUrl: string
}

export class FindApplicationsResponse implements IFindApplicationsResponse {
    success: boolean
    applications: Application[]
    pagination: Pagination
    relativeUrl: string;

    constructor(data?: IFindApplicationsResponse) {
        if (data) {
            for (let property in data) {
                if (data.hasOwnProperty(property))
                    (<any>this)[property] = (<any>data)[property];
            }
        }
    }

    init(_data?: any) {
        if (_data) {
            for (let property in _data) {
                if (_data.hasOwnProperty(property))
                    (<any>this)[property] = (<any>_data)[property];
            }

            if (_data['applications']) {
                this.applications = _data['applications'].map(item => Application.fromJS(item));
            }
            if (_data['pagination']) {
                this.pagination = (new Pagination()).init(_data['pagination']);
            }
        }
    }

    static fromJS(data: any): FindApplicationsResponse {
        data = typeof data === 'object' ? data : {};
        let result = new FindApplicationsResponse();
        result.init(data);
        return result;
    }
}

export interface IGetApplicationHistoryResponse {
    success: boolean,
    applicationHistory: WorkflowHistoryItem[]
}

export class GetApplicationHistoryResponse extends CommonBaseModel implements IGetApplicationHistoryResponse {
    success: boolean
    applicationHistory: WorkflowHistoryItem[]

    init(_data?: any): this {
        super.init(_data);
        if (_data) {
            if (_data["ApplicationHistory"]) {
                this.applicationHistory = _data['applicationHistory'].map(item => WorkflowHistoryItem.fromJS(item));
            }
        }

        return this;
    }
}

export interface IGetApplicationSummaryResponse {
    success: boolean,
    summary: { [key: string]: number; }
}

export class GetApplicationSummaryResponse implements IGetApplicationSummaryResponse {
    success: boolean
    summary: { [key: string]: number; }

    constructor(data?: IGetApplicationSummaryResponse) {
        if (data) {
            for (var property in data) {
                if (data.hasOwnProperty(property))
                    (<any>this)[property] = (<any>data)[property];
            }
        }
    }

    init(_data?: any) {
        if (_data) {
            for (var property in _data) {
                if (_data.hasOwnProperty(property))
                    (<any>this)[property] = (<any>_data)[property];
            }
        }
    }

    static fromJS(data: any): GetApplicationSummaryResponse {
        data = typeof data === 'object' ? data : {};
        let result = new GetApplicationSummaryResponse();
        result.init(data);
        return result;
    }
}

export interface ISaveApplicationBySearchControlResponse {
    success: boolean
}

export class SaveApplicationBySearchControlResponse implements ISaveApplicationBySearchControlResponse {
    success: boolean

    constructor(data?: ISaveApplicationBySearchControlResponse) {
        if (data) {
            for (var property in data) {
                if (data.hasOwnProperty(property))
                    (<any>this)[property] = (<any>data)[property];
            }
        }
    }

    init(_data?: any) {
        if (_data) {
            for (var property in _data) {
                if (_data.hasOwnProperty(property))
                    (<any>this)[property] = (<any>_data)[property];
            }
        }
    }

    static fromJS(data: any): SaveApplicationBySearchControlResponse {
        data = typeof data === 'object' ? data : {};
        let result = new SaveApplicationBySearchControlResponse();
        result.init(data);
        return result;
    }
}

export interface ILastVisitedPage {
    success: boolean;
    pageId: number | null;
    subPageId: number | null;
}

export class LastVisitedPage implements ILastVisitedPage {
    success: boolean;
    pageId: number | null;
    subPageId: number | null;

    constructor(data?: ILastVisitedPage) {
        if (data) {
            for (let property in data) {
                if (data.hasOwnProperty(property))
                    (<any>this)[property] = (<any>data)[property];
            }
        }
    }

    init(_data?: any): this {
        if (_data) {
            this.success = _data["Success"];
            this.pageId = _data["PageId"];
            this.subPageId = _data["SubPageId"];
        }

        return this;
    }

    static fromJS(data: any): LastVisitedPage {
        data = typeof data === 'object' ? data : {};
        let result = new LastVisitedPage();
        result.init(data);
        return result;
    }

    toJSON(data?: any) {
        data = typeof data === 'object' ? data : {};
        data["Success"] = this.success;
        data["PageId"] = this.pageId;
        data["SubPageId"] = this.subPageId;
        return data;
    }
}

export interface IGetApplicationGuestResponse {
    success: boolean,
    guestUsers: GuestUser[]
}

export class GetApplicationGuestResponse extends CommonBaseModel implements IGetApplicationGuestResponse {
    success: boolean
    guestUsers: GuestUser[]

    init(_data?: any): this {
        super.init(_data);
        if (_data) {
            if (_data["guestUsers"]) {
                this.guestUsers = _data['guestUsers'].map(guestUser => GuestUser.fromJS(guestUser));
            }
        }

        return this;
    }

    static fromJS(data: any): GetApplicationGuestResponse {
        data = typeof data === 'object' ? data : {};
        let result = new GetApplicationGuestResponse();
        result.init(data);
        return result;
    }
}
